// Module-level variable to track in-flight auth request for deduplication
let _inflightAuthRequest = null;
const GRAPHQL_ENDPOINT = 'https://restapi.ordergroove.com/graphql/2026-01/';

/** Shared TTL for subscriber snapshot (`dsc_subscriber_cache_*`), swappable-groups (`dsc_swappable_groups_*`), and OG REST product cache (`dsc_og_products_*`). */
export const SUBSCRIBER_CACHE_TTL_MS = 60 * 60 * 1000;
/** Club benefits discount propagation can lag subscription mutations by ~5 minutes. */
export const CLUB_BENEFITS_PROPAGATION_DELAY_MS = 5 * 60 * 1000;

const getCachedSubscriberSnapshot = () => {
  const snapshot = window?.dsc?.subscriberCache;
  if (!snapshot) return null;
  if (String(snapshot.customerId) !== String(window?.dsc?.customerId)) return null;
  return snapshot;
};

/** Coalesce post-invalidation rehydrate: multiple sync invalidates (e.g. add + create) → one microtask. */
let _subscriberRehydrateAfterInvalidateQueued = false;

const invalidateSubscriberCache = (reason) => {
  try {
    if (window?.dsc?.clearSubscriberCache) {
      window.dsc.clearSubscriberCache(reason);
    }
    const hasCustomer = Boolean(window?.dsc?.customerId && window.dsc.customerId !== 'null');
    const hasEnsure = typeof window?.dsc?.ensureSubscriberHydrated === 'function';
    if (!hasCustomer || !hasEnsure) {
      return;
    }
    if (_subscriberRehydrateAfterInvalidateQueued) {
      return;
    }
    _subscriberRehydrateAfterInvalidateQueued = true;
    queueMicrotask(async () => {
      _subscriberRehydrateAfterInvalidateQueued = false;
      try {
        await window.dsc.ensureSubscriberHydrated({ force: true });
      } catch {
        // best-effort rehydrate
      }
    });
  } catch {
    // best-effort invalidation
  }
};

const generateOGAuthorization = () => {
  const signature = JSON.stringify({
    public_id: window.OG.store.getState().merchantId,
    sig_field: window.dsc.ogAuthSignature.customerId,
    ts: window.dsc.ogAuthSignature.timestamp,
    sig: window.dsc.ogAuthSignature.signature,
  })
  return signature
}

export const refreshOGAuth = async () => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return
  }

  // Check if auth request is already in-flight - return existing promise to deduplicate
  if (_inflightAuthRequest) {
    return _inflightAuthRequest;
  }

  const ogAuthUrl = `https://${window.location.hostname}${window.dsc.ogAuth}`

  // Create the request and store it for deduplication
  _inflightAuthRequest = (async () => {
    try {
      const response = await fetch(ogAuthUrl, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to refresh OG Auth. Status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      const main = doc.querySelector('main')
      const ogAuthString = main.innerHTML
      const parsedOGAuthCookie = ogAuthString.split('og_auth_begin')[1].split('og_auth_end')[0]

      const cleanedJsonString = parsedOGAuthCookie.trim().replace(/<\/?a[^>]*>/g, '')
      const parsedJSONOGAuthCookie = JSON.parse(cleanedJsonString)
      window.dsc = window.dsc || {}
      window.dsc.ogAuthSignature = {
        ...parsedJSONOGAuthCookie,
        signature: btoa(parsedJSONOGAuthCookie.signature),
      }
      const ogAuthCookie = Object.values(window.dsc.ogAuthSignature).join('|')
      document.cookie = `og_auth=${ogAuthCookie};path=/`
      return response
    } finally {
      // Clear the in-flight request when complete (success or failure)
      _inflightAuthRequest = null;
    }
  })();

  return _inflightAuthRequest;
}

async function fetchWrapper(url, options = {}) {
  if (!window?.dsc?.customerId || window?.dsc?.customerId === 'null') {
    throw new Error('Customer is not logged in.')
  }

  await refreshOGAuth();

  // Build headers after refresh so generateOGAuthorization() never runs before ogAuthSignature exists.
  const { headers: callerHeaders = {}, ...rest } = options;
  const headers = {
    ...callerHeaders,
    Authorization: generateOGAuthorization(),
  };

  const response = await fetch(url, { ...rest, headers })

  if (response.status > 400 && response.status < 600) {
    throw response
  }

  const parsedResponse = await response.json()
  return parsedResponse
}

export const fetchOGGraphQL = async (query, variables = {}) => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return null;
  }

  await refreshOGAuth();

  const response = await fetch(GRAPHQL_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: generateOGAuthorization(),
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  });

  const text = await response.text();
  let parsedResponse;
  try {
    parsedResponse = text ? JSON.parse(text) : {};
  } catch (parseError) {
    throw new Error(
      `Ordergroove GraphQL response was not valid JSON (status ${response.status})`,
      { cause: parseError },
    );
  }

  if (!response.ok) {
    throw new Error(
      `Ordergroove GraphQL request failed with status ${response.status}: ${text.slice(0, 200)}`,
    );
  }

  if (Array.isArray(parsedResponse?.errors) && parsedResponse.errors.length > 0) {
    const firstError = parsedResponse.errors[0]?.message || 'Unknown GraphQL error';
    throw new Error(`Ordergroove GraphQL error: ${firstError}`);
  }

  return parsedResponse?.data || null;
};

export const getOGCustomer = async (customerId) => {
  if (!window.dsc || !window.dsc.customerId || window?.dsc?.customerId === 'null') {
    return;
  }

  const snapshot = getCachedSubscriberSnapshot();
  if (snapshot?.customer) {
    return {
      public_id: customerId,
    };
  }

  if (!window.OG) {
    return;
  }

  await refreshOGAuth();

  const response = await fetch(`https://restapi.ordergroove.com/customers/${customerId}/`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: generateOGAuthorization(),
    },
  })

  if (response.status === 404) {
    // there is no customer
    return
  }

  if (response.status > 400 && response.status < 600) {
    throw response
  }

  const parsedResponse = await response.json()
  return parsedResponse
}

export const getOGUpcomingOrders = async () => {
  if (!window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }

  const snapshot = getCachedSubscriberSnapshot();
  if (snapshot?.upcomingOrders) {
    return {
      count: snapshot.upcomingOrders.length,
      results: snapshot.upcomingOrders,
    };
  }

  if (!window.OG) {
    return;
  }

  return await fetchWrapper('https://restapi.ordergroove.com/orders?status=1', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

export const getOGOrdersWithStatus = async () => {
  if (!window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }

  const snapshot = getCachedSubscriberSnapshot();
  if (snapshot?.ordersWithStatus) {
    return {
      count: snapshot.ordersWithStatus.length,
      results: snapshot.ordersWithStatus,
    };
  }

  if (!window.OG) {
    return;
  }

  return await fetchWrapper('https://restapi.ordergroove.com/orders?status=6&status=1&status=18&ordering=place&include_has_plan=true', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

export const getOGOrder = async (orderId) => {
  if (!window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }

  const snapshot = getCachedSubscriberSnapshot();
  if (snapshot?.allOrderItems) {
    const filteredItems = snapshot.allOrderItems.filter((item) => String(item.order) === String(orderId));
    if (filteredItems.length > 0) {
      return {
        count: filteredItems.length,
        results: filteredItems,
      };
    }
  }

  if (snapshot?.nextOrderItems) {
    const filteredNextOrderItems = snapshot.nextOrderItems.filter((item) => String(item.order) === String(orderId));
    if (filteredNextOrderItems.length > 0) {
      return {
        count: filteredNextOrderItems.length,
        results: filteredNextOrderItems,
      };
    }
  }

  if (!window.OG) {
    return;
  }

  return await fetchWrapper(`https://restapi.ordergroove.com/items/?order=${orderId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

export const getAllOrderItems = async () => {
  if (!window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }

  const snapshot = getCachedSubscriberSnapshot();
  if (snapshot?.allOrderItems) {
    return {
      count: snapshot.allOrderItems.length,
      results: snapshot.allOrderItems,
    };
  }

  if (!window.OG) {
    return;
  }

  return await fetchWrapper(`https://restapi.ordergroove.com/items/?page_size=100&status=6&status=1&status=18&ordering=subscription`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: generateOGAuthorization(),
    },
  })
}

// takes an object with orderId, subscribedProducts, placeDate, status
export const getOGItems = async ({orderId, products, placeDate, status, subscriptions}) => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }

  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/items/?order=${orderId}&page_size=100&ordering=subscription`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
    });

    if (response.status === 404) {
      return null;
    }
    if (response.status > 400 && response.status < 600) {
      throw response;
    }
    const parsedResponse = await response.json();
    if (!parsedResponse.results) {
      return [];
    }
    var nextOrderItems = parsedResponse.results.map(item => {
      const sub = subscriptions.find(subscription => subscription.public_id === item.subscription);
      return {
        ...item,
        productObj: products.find(x => x.external_product_id === item.product),
        place: placeDate,
        status: status,
        every: sub ? sub.every : undefined,
      }
    });
    return nextOrderItems;
  } catch (error) {
    throw new Error('Error fetching Ordergroove items:', { cause: error });
  }
}

export const getAllOGProducts = async (productIds) => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }

  try {
    await refreshOGAuth();

    // product ids should be joined with '&'
    const response = await fetch(`https://restapi.ordergroove.com/products/?external_product_ids[]=${productIds}&page_size=100`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
    });

    if (response.status === 404) {
      return null;
    }
    if (response.status > 400 && response.status < 600) {
      throw response;
    }
    const parsedResponse = await response.json();

    if (parsedResponse && parsedResponse.results) {
      // The OG API returns the id as 'external_product_id', so we map it to 'id' for consistency
      return {
        ...parsedResponse,
        results: parsedResponse.results.map(product => ({
          ...product,
          id: product.external_product_id,
        })),
      };
    }
    return parsedResponse;
  } catch (error) {
    throw new Error('Error fetching Ordergroove products:', { cause: error });
  }
}

const SWAPPABLE_GROUPS_CACHE_VERSION = 1;

/** Canonical key for sku_swap group lookups (trimmed). Avoids cache misses vs stored localStorage keys. */
const normalizeSwappableGroupKey = (name) => {
  if (name == null || name === '') return '';
  return String(name).trim();
};

const swappableGroupsStorageKey = (customerId) => `dsc_swappable_groups_${customerId}`;

const findSwappableEntryInDoc = (doc, canonicalKey) => {
  if (!doc?.entries || !canonicalKey) return null;
  if (Object.prototype.hasOwnProperty.call(doc.entries, canonicalKey)) {
    return doc.entries[canonicalKey];
  }
  for (const k of Object.keys(doc.entries)) {
    if (normalizeSwappableGroupKey(k) === canonicalKey) {
      return doc.entries[k];
    }
  }
  return null;
};

const findSwappableInMemory = (mem, canonicalKey) => {
  if (!mem || !canonicalKey) return null;
  if (Object.prototype.hasOwnProperty.call(mem, canonicalKey)) {
    return { key: canonicalKey, results: mem[canonicalKey] };
  }
  for (const k of Object.keys(mem)) {
    if (normalizeSwappableGroupKey(k) === canonicalKey) {
      return { key: k, results: mem[k] };
    }
  }
  return null;
};

const pruneExpiredSwappableEntries = (entries) => {
  if (!entries || typeof entries !== 'object') return {};
  const now = Date.now();
  const out = {};
  Object.keys(entries).forEach((groupName) => {
    const entry = entries[groupName];
    if (!entry?.expiresAt) return;
    const t = new Date(entry.expiresAt).getTime();
    if (!Number.isNaN(t) && t > now) {
      out[groupName] = entry;
    }
  });
  return out;
};

const readSwappableGroupsDoc = (customerId) => {
  if (!customerId) return null;
  try {
    const raw = localStorage.getItem(swappableGroupsStorageKey(customerId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== SWAPPABLE_GROUPS_CACHE_VERSION || String(parsed.customerId) !== String(customerId)) {
      return null;
    }
    const pruned = pruneExpiredSwappableEntries(parsed.entries);
    if (Object.keys(pruned).length !== Object.keys(parsed.entries || {}).length) {
      parsed.entries = pruned;
      try {
        localStorage.setItem(swappableGroupsStorageKey(customerId), JSON.stringify(parsed));
      } catch {
        // quota
      }
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeSwappableGroupsDoc = (customerId, doc) => {
  try {
    localStorage.setItem(swappableGroupsStorageKey(customerId), JSON.stringify(doc));
  } catch {
    // localStorage unavailable or quota
  }
};

const persistSwappableGroup = (customerId, groupName, results) => {
  const key = normalizeSwappableGroupKey(groupName);
  if (!customerId || !key) return;
  let doc = readSwappableGroupsDoc(customerId);
  if (!doc) {
    doc = {
      version: SWAPPABLE_GROUPS_CACHE_VERSION,
      customerId: String(customerId),
      entries: {},
    };
  }
  doc.entries = pruneExpiredSwappableEntries(doc.entries);
  doc.entries[key] = {
    results: Array.isArray(results) ? results : [],
    expiresAt: new Date(Date.now() + SUBSCRIBER_CACHE_TTL_MS).toISOString(),
  };
  writeSwappableGroupsDoc(customerId, doc);
};

/** Remove persisted sku_swap group lists (e.g. when subscriber cache clears). */
export const clearSwappableGroupsLocalStorage = (customerId) => {
  try {
    if (customerId) {
      localStorage.removeItem(swappableGroupsStorageKey(String(customerId)));
    } else {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith('dsc_swappable_groups_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    }
  } catch {
    // ignore
  }
};

/**
 * Warm window.dsc.swappableItemsByGroup from localStorage (1h TTL) after subscriber hydration.
 */
export const hydrateSwappableGroupsFromLocalStorage = (customerId) => {
  if (!customerId || !window?.dsc) return;
  const doc = readSwappableGroupsDoc(customerId);
  if (!doc?.entries) return;
  window.dsc.swappableItemsByGroup = window.dsc.swappableItemsByGroup || {};
  Object.entries(doc.entries).forEach(([name, entry]) => {
    const exp = new Date(entry.expiresAt).getTime();
    if (!Number.isNaN(exp) && exp > Date.now() && Array.isArray(entry.results)) {
      const canonical = normalizeSwappableGroupKey(name);
      if (canonical) {
        window.dsc.swappableItemsByGroup[canonical] = entry.results;
      }
    }
  });
};

const OG_PRODUCTS_CACHE_VERSION = 1;

const ogProductsStorageKey = (customerId) => `dsc_og_products_${customerId}`;

const pruneExpiredOgProductEntries = (entries) => {
  if (!entries || typeof entries !== 'object') return {};
  const now = Date.now();
  const out = {};
  Object.keys(entries).forEach((productId) => {
    const entry = entries[productId];
    if (!entry?.expiresAt) return;
    const t = new Date(entry.expiresAt).getTime();
    if (!Number.isNaN(t) && t > now) {
      out[productId] = entry;
    }
  });
  return out;
};

const readOgProductsDoc = (customerId) => {
  if (!customerId) return null;
  try {
    const raw = localStorage.getItem(ogProductsStorageKey(customerId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== OG_PRODUCTS_CACHE_VERSION || String(parsed.customerId) !== String(customerId)) {
      return null;
    }
    const pruned = pruneExpiredOgProductEntries(parsed.entries);
    if (Object.keys(pruned).length !== Object.keys(parsed.entries || {}).length) {
      parsed.entries = pruned;
      try {
        localStorage.setItem(ogProductsStorageKey(customerId), JSON.stringify(parsed));
      } catch {
        // quota
      }
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeOgProductsDoc = (customerId, doc) => {
  try {
    localStorage.setItem(ogProductsStorageKey(customerId), JSON.stringify(doc));
  } catch {
    // localStorage unavailable or quota
  }
};

const persistOgProductToRestCache = (customerId, productId, data) => {
  const id = String(productId);
  if (!customerId || !id || data == null) return;
  let doc = readOgProductsDoc(customerId);
  if (!doc) {
    doc = {
      version: OG_PRODUCTS_CACHE_VERSION,
      customerId: String(customerId),
      entries: {},
    };
  }
  doc.entries = pruneExpiredOgProductEntries(doc.entries);
  doc.entries[id] = {
    data,
    expiresAt: new Date(Date.now() + SUBSCRIBER_CACHE_TTL_MS).toISOString(),
  };
  writeOgProductsDoc(customerId, doc);
};

/** Remove persisted per-product REST payloads (e.g. when subscriber cache clears). */
export const clearOgProductsLocalStorage = (customerId) => {
  try {
    if (customerId) {
      localStorage.removeItem(ogProductsStorageKey(String(customerId)));
    } else {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith('dsc_og_products_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    }
  } catch {
    // ignore
  }
};

/**
 * Warm window.dsc.ogProductsById from localStorage (same TTL as subscriber snapshot) after subscriber hydration.
 */
export const hydrateOgProductsFromLocalStorage = (customerId) => {
  if (!customerId || !window?.dsc) return;
  const doc = readOgProductsDoc(customerId);
  if (!doc?.entries) return;
  window.dsc.ogProductsById = window.dsc.ogProductsById || {};
  Object.entries(doc.entries).forEach(([pid, entry]) => {
    const exp = new Date(entry.expiresAt).getTime();
    if (!Number.isNaN(exp) && exp > Date.now() && entry.data != null) {
      window.dsc.ogProductsById[pid] = entry.data;
    }
  });
};

export const getAllSwappableItemsForProduct = async (group_name) => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }

  const customerId = String(window.dsc.customerId);
  const canonicalKey = normalizeSwappableGroupKey(group_name);
  if (!canonicalKey) {
    return;
  }

  window.dsc.swappableItemsByGroup = window.dsc.swappableItemsByGroup || {};
  const memoryHit = findSwappableInMemory(window.dsc.swappableItemsByGroup, canonicalKey);
  if (memoryHit) {
    if (memoryHit.key !== canonicalKey) {
      window.dsc.swappableItemsByGroup[canonicalKey] = memoryHit.results;
    }
    return memoryHit.results;
  }

  const doc = readSwappableGroupsDoc(customerId);
  const cachedEntry = findSwappableEntryInDoc(doc, canonicalKey);
  if (cachedEntry?.expiresAt && Array.isArray(cachedEntry.results)) {
    const exp = new Date(cachedEntry.expiresAt).getTime();
    if (!Number.isNaN(exp) && exp > Date.now()) {
      window.dsc.swappableItemsByGroup[canonicalKey] = cachedEntry.results;
      return cachedEntry.results;
    }
  }

  try {
    await refreshOGAuth();

    const response = await fetch(
      `https://restapi.ordergroove.com/products/?page_size=100&group_name=${encodeURIComponent(canonicalKey)}&group_type=sku_swap`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: generateOGAuthorization(),
        },
      },
    );

    if (response.status === 404) {
      return null;
    }
    if (response.status > 400 && response.status < 600) {
      throw response;
    }
    const parsedResponse = await response.json();
    const results = parsedResponse.results || [];
    window.dsc.swappableItemsByGroup[canonicalKey] = results;
    persistSwappableGroup(customerId, canonicalKey, results);
    return results;
  } catch (error) {
    throw new Error('Error fetching Ordergroove items:', { cause: error });
  }
}

export const addItemToNextBox = async (payload) => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }

  const offer = payload.offer || window.dsc.ogOffer

  const result = await fetchWrapper('https://restapi.ordergroove.com/items/iu/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      offer: offer,
      quantity: 1,
      ...payload,
    }),
  });

  if (result?.public_id) {
    invalidateSubscriberCache('mutation:add-item-to-next-box');
  }

  return result;
}


/**
 * OrderGroove `change_next_order_date` expects a calendar date (YYYY-MM-DD), not a full ISO-8601 datetime.
 * GraphQL order `place` values are often ISO with offset; REST used "YYYY-MM-DD HH:mm:ss".
 */
export const normalizeOrderDateForOgApi = (value) => {
  if (value == null || value === '') return value;
  const s = String(value).trim();
  if (s.includes('T')) return s.split('T')[0];
  if (s.includes(' ')) return s.split(' ')[0];
  return s;
};

//change sub delivery Date
export const changeSubDeliveryDate = async (subscriptionId, newOrderDate) => {
  if (!subscriptionId) {
    throw new Error('Subscription ID is required for change subscription delivery date API call');
  }

  try {
    await refreshOGAuth();

    const orderDate = normalizeOrderDateForOgApi(newOrderDate);

    const response = await fetch(`https://restapi.ordergroove.com/subscriptions/${subscriptionId}/change_next_order_date/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
      body: JSON.stringify({
        order_date: orderDate,
      }),
    });
    
    const responseData = await response.json();
    
    const result = {
      success: response.status === 200,
      status: response.status,
      data: responseData,
    };
    if (result.success) {
      invalidateSubscriberCache('mutation:change-sub-delivery-date');
    }
    return result;
  } catch (error) {
    throw new Error('Failed to change subscription delivery date:', { cause: error });
  }
};

// Send Now - Sends an order to the merchant, setting order placement within a 24-hour window
// https://developer.ordergroove.com/reference/orders-send-now
export const sendOGOrderNow = async (orderId) => {
  if (!orderId) {
    throw new Error('Order ID is required for send now API call');
  }

  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/orders/${orderId}/send_now`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      }
    });
    
    const responseData = await response.json();
    
    const result = { success: true, data: responseData, error: null, status: response?.status };
    if (response?.ok) {
      invalidateSubscriberCache('mutation:send-order-now');
    }
    return result;
  } catch (error) {
    throw new Error('Failed to send order now:', { cause: error });
  }
};

// Skip Subscription - Skips the order
export const skipOGOrder = async (order_id) => {
  if (!order_id) {
    throw new Error('Order ID is required for skip order API call');
  }

  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/orders/${order_id}/cancel/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      }
    });
    
    const responseData = await response.json();
    
    const result = {
      success: response.status === 200,
      status: response.status,
      data: responseData
    };
    if (result.success) {
      invalidateSubscriberCache('mutation:skip-order');
    }
    return result;
  } catch (error) {
    throw new Error('Failed to skip order:', { cause: error });
  }
};

export const changeOrderPlaceDate = async (orderId, newPlaceDate) => {
  if (!orderId) {
    throw new Error('Order ID is required for change order place date API call');
  }

  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/orders/${orderId}/change_place_date/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
      body: JSON.stringify({
        place: newPlaceDate,
      }),
    });
    
    const responseData = await response.json();
    
    const result = {
      success: response.status === 200,
      status: response.status,
      data: responseData,
    };
    if (result.success) {
      invalidateSubscriberCache('mutation:change-order-place-date');
    }
    return result;
  } catch (error) {
    throw new Error('Failed to change order place date:', { cause: error });
  }
};

/**
 * Updates club benefits state after a subscription action (e.g. create, reactivate).
 * Increments activeSubscriptionsCount, sets clubBenefitsSubActionTaken when applicable,
 * persists to localStorage, and dispatches club-benefits-data-ready for reactive UI.
 * @param {number} [activeSubscriptionsCountDelta=1] - Amount to add to activeSubscriptionsCount.
 */
const updateClubBenefitsSubActionTaken = (activeSubscriptionsCountDelta = 1) => {
  window.dsc = window.dsc || {};
  window.dsc.activeSubscriptionsCount = (window.dsc.activeSubscriptionsCount || 0) + activeSubscriptionsCountDelta;

  if (window.dsc.activeSubscriptionsCount <= 0) {
    window.dsc.activeSubscriptionsCount = 0;
    // Do not reset clubBenefitsSubActionTaken when count goes to zero; user stays in unlocked state
  } else if (window.dsc.clubBenefitsSubActionTaken !== true) {
    window.dsc.clubBenefitsSubActionTaken = true;
    try { localStorage.setItem('dsc_clubBenefitsSubActionTaken', 'true'); } catch (e) { /* localStorage unavailable */ }
  }
  try {
    localStorage.setItem(
      'dsc_club_benefits_pending_until',
      String(Date.now() + CLUB_BENEFITS_PROPAGATION_DELAY_MS),
    );
  } catch (e) {
    // localStorage unavailable
  }

  document.dispatchEvent(
    new CustomEvent('club-benefits-data-ready', {
      detail: {
        activeSubscriptionsCount: window.dsc.activeSubscriptionsCount,
        clubBenefitsSubActionTaken: window.dsc.clubBenefitsSubActionTaken,
      },
    }),
  );
};

export const createFromItem = async (payload) => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }

  const offer = payload.offer || window.dsc.ogOffer;

  const result = await fetchWrapper('https://restapi.ordergroove.com/subscriptions/create_from_item/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      offer: offer,
      every_period: 3, // 3 is the enum for month
      ...payload,
    }),
  });

  if (result) {
    updateClubBenefitsSubActionTaken(1);
    invalidateSubscriberCache('mutation:create-from-item');
  }

  return result;
}

export const reactivateSubscription = async (actionToken) => {
  await refreshOGAuth();

  const response = await fetch(`https://restapi.ordergroove.com/one_click/reactivate?token=${actionToken}`, {
    method: 'GET',
    headers: {
      accept: 'application/json',
    },
  });

  if (response?.ok) {
    updateClubBenefitsSubActionTaken(1);
    invalidateSubscriberCache('mutation:reactivate-one-click');
  }

  return response;
};

export const getFrequencyTypeEnum = (frequencyTypeString) => {
  switch (frequencyTypeString) {
    case 'month':
    case 'months':
      return 3;
      case 'week':
      case 'weeks':
        return 2;
      case 'day':
      case 'days':
        return 1;
      default:
        return 3;
  }
}

export const upsellProduct = async ({ upsellType, productId, actionToken, frequencyDescription }) => {
  if (upsellType === 'one-time-upsell') {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/one_click/upsell/?product_id=${productId}&token=${actionToken}`, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    });
    if (response?.ok) {
      invalidateSubscriberCache('mutation:upsell-one-click');
    }
    return response;
  } else if (upsellType === 'subscription-upsell') {
    const frequencyDescriptionSplit = frequencyDescription.split(' ');
    const frequency = frequencyDescriptionSplit[0];
    const frequencyType = getFrequencyTypeEnum(frequencyDescriptionSplit[1]);

    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/one_click/subscription_upsell/?product_id=${productId}&every=${frequency}&every_period=${frequencyType}&token=${actionToken}`, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    });
    if (response?.ok) {
      invalidateSubscriberCache('mutation:subscription-upsell-one-click');
    }
    return response;
  }
};

// Fetch Ordergroove product info by productId
// https://developer.ordergroove.com/reference/products-list
export const getOGProduct = async (productId) => {
  if (!window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }

  const customerId = String(window.dsc.customerId);
  const normalizedId = String(productId);
  const snapshot = getCachedSubscriberSnapshot();
  if (snapshot?.products && snapshot.products[normalizedId]) {
    return snapshot.products[normalizedId];
  }

  const doc = readOgProductsDoc(customerId);
  const cachedEntry = doc?.entries?.[normalizedId];
  if (cachedEntry?.data != null && cachedEntry?.expiresAt) {
    const exp = new Date(cachedEntry.expiresAt).getTime();
    if (!Number.isNaN(exp) && exp > Date.now()) {
      window.dsc.ogProductsById = window.dsc.ogProductsById || {};
      window.dsc.ogProductsById[normalizedId] = cachedEntry.data;
      if (snapshot?.products && snapshot.products !== window.dsc.ogProductsById) {
        snapshot.products[normalizedId] = cachedEntry.data;
      }
      return cachedEntry.data;
    }
  }

  if (!window.OG) {
    return;
  }

  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/products/${productId}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
    });
    if (response.status === 404) {
      // No product found
      return null;
    }
    if (response.status > 400 && response.status < 600) {
      throw response;
    }
    const parsedResponse = await response.json();
    persistOgProductToRestCache(customerId, normalizedId, parsedResponse);
    window.dsc.ogProductsById = window.dsc.ogProductsById || {};
    window.dsc.ogProductsById[normalizedId] = parsedResponse;
    if (snapshot?.products && snapshot.products !== window.dsc.ogProductsById) {
      snapshot.products[normalizedId] = parsedResponse;
    }
    return parsedResponse;
  } catch (error) {
    throw new Error('Error fetching Ordergroove product info:', { cause: error });
  }
}

export const sortOGOrders = (orders) => {
  orders.sort((a, b) => {
    const aOrderTime = new Date(a.place).getTime()
    const bOrderTime = new Date(b.place).getTime()
    if (aOrderTime < bOrderTime) {
      return -1
    } else if (aOrderTime > bOrderTime) {
      return 1
    } else {
      return 0
    }
  })
}

// Fetch a customer's subscriptions from Ordergroove
// https://developer.ordergroove.com/reference/subscriptions-list
export const getOGSubscriptions = async (customerId) => {
  if (!window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }

  const snapshot = getCachedSubscriberSnapshot();
  if (snapshot?.subscriptions) {
    return {
      count: snapshot.subscriptions.length,
      results: snapshot.subscriptions,
    };
  }

  if (!window.OG) {
    return;
  }

  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/subscriptions/?customer=${customerId}&page_size=100`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
    });

    if (response.status === 404) {
      // No subscriptions found for this customer
      return null;
    }
    if (response.status > 400 && response.status < 600) {
      throw response;
    }
    const parsedResponse = await response.json();
    return parsedResponse;
  } catch (error) {
    throw new Error('Error fetching Ordergroove subscriptions:', { cause: error });
  }
}

/**
 * Same as GET subscriptions list as getOGSubscriptions, but never reads subscriber cache.
 * Used to enrich the GraphQL subscriber snapshot with REST-only fields (e.g. offer id).
 */
export const fetchRestSubscriptionsList = async (customerId) => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return null;
  }

  await refreshOGAuth();

  const response = await fetch(
    `https://restapi.ordergroove.com/subscriptions/?customer=${customerId}&page_size=200`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
    },
  );

  if (response.status === 404) {
    return null;
  }
  if (response.status > 400 && response.status < 600) {
    throw new Error(`Ordergroove subscriptions REST request failed with status ${response.status}`);
  }
  return response.json();
}

export const changeOGSubscriptionFrequency = async (subscriptionId, frequency) => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }

  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/subscriptions/${subscriptionId}/change_frequency/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
      body: JSON.stringify({
        every: parseInt(frequency),
        every_period: 3, // 3 is the enum for month
      }),
    });
    
    const responseData = await response.json();
    
    const result = {
      success: response.status === 200,
      status: response.status,
      data: responseData,
    };
    if (result.success) {
      invalidateSubscriberCache('mutation:change-subscription-frequency');
    }
    return result;
  } catch (error) {
    throw new Error('Failed to change subscription frequency:', { cause: error });
  }
};

export const changeOGSubscriptionQuantity = async (subscriptionId, quantity) => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }

  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/subscriptions/${subscriptionId}/change_quantity/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
      body: JSON.stringify({
        quantity: parseInt(quantity),
      }),
    });
    
    const responseData = await response.json();
    
    const result = {
      success: response.status === 200,
      status: response.status,
      data: responseData,
    };
    if (result.success) {
      invalidateSubscriberCache('mutation:change-subscription-quantity');
    }
    return result;
  } catch (error) {
    throw new Error('Failed to change subscription quantity:', { cause: error });
  }
};

export const changeOGItemQuantity = async (itemId, quantity) => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }

  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/items/${itemId}/change_quantity/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
      body: JSON.stringify({
        quantity: parseInt(quantity),
      }),
    });
    
    const responseData = await response.json();
    
    const result = {
      success: response.status === 200,
      status: response.status,
      data: responseData,
    };
    if (result.success) {
      invalidateSubscriberCache('mutation:change-item-quantity');
    }
    return result;
  } catch (error) {
    throw new Error('Failed to change item quantity:', { cause: error });
  }
};

export const reactivateOGSubscription = async (subscriptionId, frequency) => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }

  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/subscriptions/${subscriptionId}/reactivate/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
      body: JSON.stringify({
        every: parseInt(frequency),
        every_period: 3, // 3 is the enum for month
        start_date: new Date().toISOString().split('T')[0],
      }),
    });
    
    const responseData = await response.json();

    if (response.status === 200) {
      updateClubBenefitsSubActionTaken(1);
      invalidateSubscriberCache('mutation:reactivate-subscription');
    }

    return {
      success: response.status === 200,
      status: response.status,
      data: responseData,
    };
  } catch (error) {
    throw new Error('Failed to reactivate subscription:', { cause: error });
  }
};

export const getOGShippingAddresses = async () => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }
  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/addresses/?customer=${window.dsc.customerId}&page_size=100`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
    });
    
    const responseData = await response.json();
    
    return {
      success: response.status === 200,
      status: response.status,
      data: responseData,
    };
  } catch (error) {
    throw new Error('Failed to get shipping address in ordergroove-api-helper:', { cause: error });
  }
};

export const updateOGOrderShippingAddress = async (orderId, addressId) => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }
  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/orders/${orderId}/change_shipping/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
      body: JSON.stringify({
        shipping_address: addressId,
      }),
    });
    
    const responseData = await response.json();

    if (response.status !== 200) {
      return {
        success: false,
        status: response.status,
        data: responseData,
        message: 'Failed to update shipping address in ordergroove-api-helper',
      };
    }
    
    const result = {
      success: response.status === 200,
      status: response.status,
      data: responseData,
      message: 'Shipping address updated successfully',
    };
    if (result.success) {
      invalidateSubscriberCache('mutation:update-order-shipping-address');
    }
    return result;
  } catch (error) {
    throw new Error('Failed to update shipping address in ordergroove-api-helper:', { cause: error });
  }
};


export const updateShippingAddress = async (address, orderId) => {
  const formattedAddress = {
    customer: window.dsc.customerId,
    address_type: 'shipping_address',
    first_name: address.first_name || '',
    last_name: address.last_name || '',
    label: '',
    company_name: address.company || '',
    address: address.address || '',
    address2: address.address2 || '',
    city: address.city || '',
    state_province_code: address.state_province_code || '',
    zip_postal_code: address.zip_postal_code || '',
    country_code: address.country_code || '',
    phone: address.phone || '',
    fax: '',
    token_id: '',
  };
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }
  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/addresses/create/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
      body: JSON.stringify(formattedAddress),
    });
    
    const responseData = await response.json();

    if (responseData.public_id) {
      const updateResponse = await updateOGOrderShippingAddress(orderId, responseData.public_id);
      if (updateResponse.success) {
        invalidateSubscriberCache('mutation:update-shipping-address');
        return {
          success: true,
          status: updateResponse.status,
          data: responseData,
          message: 'Shipping address updated successfully',
        }
      } else{
        return {
          success: false,
          status: updateResponse.status,
          data: responseData,
          message: 'Failed to update shipping address in ordergroove-api-helper',
        };
      }
    }
    
    return {
      success: response.status === 200,
      status: response.status,
      data: responseData,
    };
  } catch (error) {
    throw new Error('Failed to create shipping address or update shipping address in ordergroove-api-helper:', { cause: error });
  }
};

export const addSubToNextBox = async ({subscriptionId, shippingAddressId, date}) => {
  // payload should include subscriptionId, addressId, date
  if (!subscriptionId) {
    return;
  }

  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/subscriptions/${subscriptionId}/change_shipping/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
      body: JSON.stringify({
        shipping_address: shippingAddressId,
      }),
    });
    
    const responseShippingData = await response.json();

    if (response.status == 200) {
      try {
        const changeDeliveryDateResponse = await changeSubDeliveryDate(subscriptionId, date);
        if (changeDeliveryDateResponse.success) {
          invalidateSubscriberCache('mutation:add-sub-to-next-box');
          return {
            success: true,
            status: response.status,
            data: changeDeliveryDateResponse,
            message: 'Shipping address and date changed successfully',
          };
        }
      } catch (error) {
        throw new Error('Failed to change delivery date in ordergroove-api-helper:', { cause: error });
      }
    }
    
    return {
      success: false,
      status: response.status,
      data: responseShippingData,
      message: 'Failed to change shipping address and date in ordergroove-api-helper',
    };
  } catch (error) {
    throw new Error('Failed to change shipping address in ordergroove-api-helper:', { cause: error });
  }
};


export const getOGCountries = async () => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }
  try {
    // Use hardcoded data for US, Canada, and Australia instead of making a fetch request
    // This avoids CORS issues with the Ordergroove CDN
    const response = await fetch(window.ogCountryDataUrl);
    const responseData = await response.json();
    // Filter for supported countries only
    const supportedCountries = ['US', 'CA', 'AU', 'GB'];
    const filteredData = {};
    for (const code of supportedCountries) {
      if (responseData[code]) filteredData[code] = responseData[code];
    }
    return {
      success: true,
      status: 200,
      data: filteredData
    };
  } catch (error) {
    return {
      success: false,
      status: 500,
      data: {}
    };
  }
};

export const getOGPaymentInfo = async (paymentId) => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }
  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/payments/${paymentId}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
    });
    
    const responseData = await response.json();
    
    return {
      success: response.status === 200,
      status: response.status,
      data: responseData,
    };
  } catch (error) {
    throw new Error('Failed to get payment info in ordergroove-api-helper:', { cause: error });
  }
};

export const removeItemFromNextBox = async (itemId) => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return {
      success: false,
      error: 'Missing customer ID or Ordergroove configuration'
    };
  }

  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/items/${itemId}/delete`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
    });
    
    // Per Ordergroove API docs, a successful delete returns 204 No Content
    if (response.status === 204) {
      invalidateSubscriberCache('mutation:remove-item-from-next-box');
      return {
        success: true
      };
    }
    
    // For error responses, try to parse the JSON if available
    let errorData = {};
    try {
      errorData = await response.json();
    } catch (e) {
      // If response can't be parsed as JSON, use status text
      errorData = { message: response.statusText };
    }
    
    return {
      success: false,
      status: response.status,
      data: errorData
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Network error: could not remove item from next box'
    };
  }
};

export const sendPaymentUpdateEmail = async () => {
  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }

  try {
    await refreshOGAuth();

    await fetch(`/apps/subscriptions/send-update-payment-email/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
    });
    
    return {
      success: true,
      status: 200,
      data: 'OK',
    };
  } catch (error) {
    return {
      success: false,
      status: 500,
      data: error.message || 'Failed to send payment update email',
    };
  }
};

//check eligible discount
export const checkIfEligibleForDiscount = async (subscriptionId, shopID) => {
    if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null' || shopID === 'null') {
        return;
    }

    try {
      // get signature from page hash
        const getSignature = await fetch(`/pages/og-sub-hash/subscription_id=${subscriptionId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        const signatureResponse = await getSignature.json();
        const signature = signatureResponse.hash;

        let apiSuffix;
        if (shopID === 66859172052) {
            apiSuffix = 'ogc-staging-us';
        }
        if (shopID === 56839438384) {
            apiSuffix = 'ogc-us';
        }

        await refreshOGAuth();

        // check if eligible for discount
        const response = await fetch(`https://${apiSuffix}.shave.io/get_discount_level?customerId=${window.dsc.customerId}&ordergrooveSubscriptionId=${subscriptionId}&signature=${signature}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: generateOGAuthorization(),
            },
        });
        const responseData = await response.json();
        return {
            success: responseData.status === 200,
            status: responseData.status,
            discount_level: responseData.discountLevel,
        };
    } catch (error) {
        return {
            success: false,
            status: 500,
            data: error.message || 'Failed to check if eligible for discount',
        };
    } 
}

export const setDiscountLevel = async (subscriptionId, discountLevel, shopID) => {
    if (!subscriptionId) {
        throw new Error('Subscription ID is required for set ordergroove discount level API call');
    }

    try {

      // get signature from page hash
      const getSignature = await fetch(`/pages/og-sub-hash/subscription_id=${subscriptionId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
      });
      const signatureResponse = await getSignature.json();
      const signature = signatureResponse.hash;

      let apiSuffix;
        if (shopID === 66859172052) {
            apiSuffix = 'ogc-staging-us';
        }
        if (shopID === 56839438384) {
            apiSuffix = 'ogc-us';
        }

      await refreshOGAuth();

      // set discount level
        const response = await fetch(`https://${apiSuffix}.shave.io/set_order_discount?customerId=${window.dsc.customerId}&ordergrooveSubscriptionId=${subscriptionId}&signature=${signature}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: generateOGAuthorization(),
            },
            body: JSON.stringify({
                discount_level: discountLevel,
            })
        });
        
        const responseData = await response.json();
        
        const result = {
            success: response.status === 200,
            status: response.status,
            data: responseData
        };
        if (result.success) {
            invalidateSubscriberCache('mutation:set-discount-level');
        }
        return result;
    } catch (error) {
        throw new Error('Failed to set discount level:', { cause: error });
    }
}

//cancel subscription
export const cancelSubscription = async (subscriptionId, cancelReason) => {
  if (!subscriptionId) {
    throw new Error('Subscription ID is required for cancel subscription API call');
  }

  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/subscriptions/${subscriptionId}/cancel/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
      body: JSON.stringify({cancel_reason: cancelReason})
    });
    
    const responseData = await response.json();
    
    const result = {
      success: response.status === 200,
      status: response.status,
      data: responseData
    };
    if (result.success) {
      invalidateSubscriberCache('mutation:cancel-subscription');
    }
    return result;
  } catch (error) {
    throw new Error('Failed to cancel subscription:', { cause: error });
  }
}


export const swapProductInSubscription = async (subscriptionId, productId) => {
  if (!subscriptionId) {
    throw new Error('Subscription ID is required for swap product in subscription API call');
  }

  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/subscriptions/${subscriptionId}/change_product/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
      body: JSON.stringify({
        product: productId,
      })
    });
    
    const responseData = await response.json();
    
    const result = {
      success: response.status === 200,
      status: response.status,
      data: responseData
    };
    if (result.success) {
      invalidateSubscriberCache('mutation:swap-product-in-subscription');
    }
    return result;
  } catch (error) {
    throw new Error('Failed to swap product in subscription:', { cause: error });
  }
}

export const currencyFormat = (amount, currencyCode) => {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode || 'USD' }).format(amount);
}



window.dsc = window.dsc || {}
window.dsc.ogSortOGOrders = sortOGOrders
window.dsc.ogCreateFromItem = createFromItem
window.dsc.ogAddItemToNextBox = addItemToNextBox
window.dsc.ogGetOgUpcomingOrders = getOGUpcomingOrders
window.dsc.currencyFormat = currencyFormat

export const updateAllSubscriptionShippingAddresses = async (address) => {
  const formattedAddress = {
    customer: window.dsc.customerId,
    address_type: 'shipping_address',
    first_name: address.first_name || '',
    last_name: address.last_name || '',
    label: '',
    company_name: address.company || '',
    address: address.address || '',
    address2: address.address2 || '',
    city: address.city || '',
    state_province_code: address.state_province_code || '',
    zip_postal_code: address.zip_postal_code || '',
    country_code: address.country_code || '',
    phone: address.phone || '',
    fax: '',
    token_id: '',
  };

  if (!window.OG || !window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
    return;
  }

  try {
    await refreshOGAuth();

    const response = await fetch(`https://restapi.ordergroove.com/addresses/create/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
      body: JSON.stringify(formattedAddress),
    });

    const responseData = await response.json();

    if (!response.ok || !responseData.public_id) {
      throw new Error('Failed to create the new shipping address.');
    }

    const useForAllResponse = await fetch(`https://restapi.ordergroove.com/addresses/${responseData.public_id}/use_for_all/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: generateOGAuthorization(),
      },
    });

    if (useForAllResponse.status !== 204) {
      const errorData = await useForAllResponse.json().catch(() => ({}));
      // eslint-disable-next-line no-console
      console.error('Failed to apply address to all subscriptions:', errorData);
      throw new Error('Failed to apply address to all subscriptions.');
    }

    const result = {
      success: true,
      status: useForAllResponse.status,
      data: responseData,
      message: 'Shipping address updated for all subscriptions successfully',
    };
    invalidateSubscriberCache('mutation:update-all-subscription-shipping-addresses');
    return result;

  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error in updateAllSubscriptionShippingAddresses:', error);
    return {
      success: false,
      message: error.message || 'An unexpected error occurred while updating all shipping addresses.',
    };
  }
};
